import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import {
  makeStyles,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Typography,
  Box,
} from "@material-ui/core";
import {
  Add as AddIcon,
} from "@material-ui/icons";
import moment from "moment";

import { i18n } from "../../translate/i18n";
import toastUtils from "../../utils/toastUtils";
import { useCompaniesContext } from "../../context/Companies/CompaniesContext";
import { useDate } from "../../hooks/useDate";
import ConfirmationModal from "../ConfirmationModal";
import FormattedCurrency from "../FormattedCurrency";
import CompanyModal from "./CompanyModal";
import ActionButtons from "./ActionButtons";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(2),
    // Screen reader only class for accessibility
    "& .sr-only": {
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: 0,
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: 0,
    },
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  headerTitle: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  newCompanyButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    "&:disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    backgroundColor: theme.palette.background.paper,
  },
  tableHeader: {
    backgroundColor: theme.palette.type === "dark" 
      ? theme.palette.grey[800] 
      : theme.palette.grey[50],
  },
  tableHeaderCell: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  tableCell: {
    color: theme.palette.text.primary,
  },
  blockedChip: {
    color: theme.palette.error.main,
    fontWeight: "bold",
  },
  activeChip: {
    color: theme.palette.success.main,
    fontWeight: "bold",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  emptyContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const CompaniesManager = () => {
  const classes = useStyles();
  const { dateToClient, datetimeToClient } = useDate();
  const { 
    companies, 
    plans, 
    loading, 
    error, 
    fetchCompaniesAndPlans, 
    saveCompany, 
    updateCompany, 
    removeCompany, 
    toggleBlockStatus, 
    cleanCompanyFiles 
  } = useCompaniesContext();

  // Refs for accessibility and focus management
  const newCompanyButtonRef = useRef(null);
  const tableRef = useRef(null);
  const lastFocusedElementRef = useRef(null);
  const containerRef = useRef(null);

  const [loadingState, setLoadingState] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  // Modal state management
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  
  // Confirmation dialogs state
  const [confirmDialogs, setConfirmDialogs] = useState({
    delete: false,
    block: false,
    clean: false,
  });

  // Operation loading states
  const [operationLoading, setOperationLoading] = useState({
    delete: false,
    block: false,
    clean: false,
  });

  const loadData = useCallback(async () => {
    setLoadingState(true);
    try {
      await fetchCompaniesAndPlans();
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      
      let errorMessage = "Erro desconhecido";
      let errorTitle = "Erro ao Carregar Dados";
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.status === 403) {
        errorMessage = "Você não tem permissão para visualizar as empresas";
        errorTitle = "Acesso Negado";
      } else if (error?.response?.status === 401) {
        errorMessage = "Sessão expirada. Faça login novamente";
        errorTitle = "Sessão Expirada";
      } else if (error?.response?.status === 500) {
        errorMessage = "Erro interno do servidor. Tente novamente em alguns minutos";
        errorTitle = "Erro do Servidor";
      } else if (error?.response?.status === 0 || error?.code === 'NETWORK_ERROR') {
        errorMessage = "Erro de conexão. Verifique sua internet e tente novamente";
        errorTitle = "Erro de Conexão";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toastUtils.error(errorMessage, errorTitle);
    } finally {
      setLoadingState(false);
    }
  }, [fetchCompaniesAndPlans]);

  useEffect(() => {
    // Data is already loaded by the context, but we can refresh if needed
    if (companies.length === 0 && plans.length === 0 && !loading) {
      loadData();
    }
  }, [companies.length, plans.length, loading, loadData]);

  // Enhanced keyboard navigation for the entire component
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Global keyboard shortcuts
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'n':
          case 'N':
            // Ctrl+N to create new company
            if (!loadingState && newCompanyButtonRef.current) {
              event.preventDefault();
              handleCreateCompany();
            }
            break;
          default:
            break;
        }
      }

      // Table navigation with arrow keys
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const activeElement = document.activeElement;
        const tableRows = tableRef.current?.querySelectorAll('tbody tr[role="row"]');
        
        if (tableRows && tableRows.length > 0) {
          const currentRow = activeElement?.closest('tr[role="row"]');
          if (currentRow) {
            const currentIndex = Array.from(tableRows).indexOf(currentRow);
            let nextIndex;
            
            if (event.key === 'ArrowDown') {
              nextIndex = Math.min(currentIndex + 1, tableRows.length - 1);
            } else {
              nextIndex = Math.max(currentIndex - 1, 0);
            }
            
            if (nextIndex !== currentIndex) {
              event.preventDefault();
              const nextRow = tableRows[nextIndex];
              const firstFocusable = nextRow.querySelector('button, [tabindex]:not([tabindex="-1"])');
              if (firstFocusable) {
                firstFocusable.focus();
              } else {
                nextRow.focus();
              }
            }
          }
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return () => {
        container.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [loadingState]);

  // Announce data loading status to screen readers
  useEffect(() => {
    if (loadingState) {
      announceToScreenReader('Carregando dados das empresas...');
    } else if (companies.length > 0) {
      announceToScreenReader(`${companies.length} empresa${companies.length > 1 ? 's' : ''} carregada${companies.length > 1 ? 's' : ''}`);
    }
  }, [loadingState, companies.length]);

  // Helper function to announce messages to screen readers
  const announceToScreenReader = (message) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);
  };

  // Modal handling methods
  const handleCreateCompany = () => {
    // Store current focus for restoration after modal closes
    lastFocusedElementRef.current = document.activeElement;
    
    setModalMode('create');
    setSelectedCompany(null);
    setModalOpen(true);
  };

  const handleEditCompany = (company) => {
    if (!company || !company.id) {
      console.error("Empresa inválida:", company);
      toastUtils.error("Empresa inválida", "Erro");
      return;
    }

    // Store current focus for restoration after modal closes
    lastFocusedElementRef.current = document.activeElement;

    setModalMode('edit');
    setSelectedCompany(company);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCompany(null);
    
    // Restore focus to the element that opened the modal
    if (lastFocusedElementRef.current && lastFocusedElementRef.current.focus) {
      setTimeout(() => {
        lastFocusedElementRef.current.focus();
      }, 100);
    }
  };

  const handleModalSubmit = async (formData) => {
    try {
      if (modalMode === 'edit' && selectedCompany?.id) {
        // Ensure the ID is included for update operations
        const updateData = { ...formData, id: selectedCompany.id };
        await updateCompany(updateData);
        toastUtils.success(
          `Empresa "${formData.name}" atualizada com sucesso`, 
          "Sucesso"
        );
      } else {
        // For create operations, ensure required fields are present
        if (!formData.name || !formData.email) {
          throw new Error("Nome e email são obrigatórios");
        }
        if (!formData.password) {
          throw new Error("Senha é obrigatória para criar empresa");
        }
        
        await saveCompany(formData);
        toastUtils.success(
          `Empresa "${formData.name}" criada com sucesso`, 
          "Sucesso"
        );
      }

      // Refresh data and close modal on success
      await loadData();
      setModalOpen(false);
      setSelectedCompany(null);
    } catch (error) {
      console.error("Erro ao salvar empresa:", error);
      
      // Extract meaningful error message from API response
      let errorMessage = "Erro desconhecido";
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Show specific error messages for common scenarios
      if (errorMessage.toLowerCase().includes("email")) {
        toastUtils.error("Email já está em uso ou é inválido", "Erro de Validação");
      } else if (errorMessage.toLowerCase().includes("name") || errorMessage.toLowerCase().includes("nome")) {
        toastUtils.error("Nome da empresa é inválido ou já existe", "Erro de Validação");
      } else if (errorMessage.toLowerCase().includes("password") || errorMessage.toLowerCase().includes("senha")) {
        toastUtils.error("Senha deve ter pelo menos 5 caracteres", "Erro de Validação");
      } else if (error?.response?.status === 409) {
        toastUtils.error("Dados conflitantes. Verifique se email ou nome já existem", "Conflito de Dados");
      } else if (error?.response?.status === 422) {
        toastUtils.error("Dados inválidos. Verifique os campos obrigatórios", "Erro de Validação");
      } else {
        toastUtils.error(errorMessage, modalMode === 'edit' ? "Erro ao Atualizar Empresa" : "Erro ao Criar Empresa");
      }
      
      throw error; // Re-throw to let modal handle the error state
    }
  };

  // Action button handlers
  const handleDeleteClick = (company) => {
    if (!company || !company.id) {
      toastUtils.error("Empresa inválida selecionada", "Erro");
      return;
    }

    if (company.id === 1) {
      toastUtils.warning(
        "A empresa principal (ID: 1) não pode ser excluída por questões de segurança",
        "Operação Não Permitida"
      );
      return;
    }

    if (loadingState) {
      toastUtils.info("Aguarde a operação atual terminar", "Operação em Andamento");
      return;
    }

    setSelectedCompany(company);
    setConfirmDialogs(prev => ({ ...prev, delete: true }));
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCompany || !selectedCompany.id) {
      toastUtils.error("Empresa não selecionada", "Erro");
      setConfirmDialogs(prev => ({ ...prev, delete: false }));
      setSelectedCompany(null);
      return;
    }

    setOperationLoading(prev => ({ ...prev, delete: true }));
    setLoadingState(true);
    
    try {
      // Use DELETE /companies/:id endpoint
      await removeCompany(selectedCompany.id);
      toastUtils.success(`Empresa "${selectedCompany.name}" excluída com sucesso`, "Sucesso");
      await loadData();
    } catch (error) {
      console.error("Erro ao excluir empresa:", error);
      
      let errorMessage = "Erro desconhecido";
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.status === 403) {
        errorMessage = "Você não tem permissão para excluir esta empresa";
      } else if (error?.response?.status === 404) {
        errorMessage = "Empresa não encontrada";
      } else if (error?.response?.status === 409) {
        errorMessage = "Não é possível excluir esta empresa pois possui dados relacionados";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toastUtils.error(errorMessage, "Erro ao Excluir Empresa");
    } finally {
      setOperationLoading(prev => ({ ...prev, delete: false }));
      setLoadingState(false);
      setConfirmDialogs(prev => ({ ...prev, delete: false }));
      setSelectedCompany(null);
    }
  };

  const handleBlockClick = (company) => {
    if (!company || !company.id) {
      toastUtils.error("Empresa inválida selecionada", "Erro");
      return;
    }

    if (company.id === 1) {
      toastUtils.warning(
        "A empresa principal (ID: 1) não pode ser bloqueada por questões de segurança",
        "Operação Não Permitida"
      );
      return;
    }

    if (loadingState) {
      toastUtils.info("Aguarde a operação atual terminar", "Operação em Andamento");
      return;
    }

    setSelectedCompany(company);
    setConfirmDialogs(prev => ({ ...prev, block: true }));
  };

  const handleBlockConfirm = async () => {
    if (!selectedCompany || !selectedCompany.id) {
      toastUtils.error("Empresa não selecionada", "Erro");
      setConfirmDialogs(prev => ({ ...prev, block: false }));
      setSelectedCompany(null);
      return;
    }

    setOperationLoading(prev => ({ ...prev, block: true }));
    setLoadingState(true);
    
    try {
      // Use PATCH /companies/:id/toggle-status endpoint
      await toggleBlockStatus(selectedCompany.id);
      const action = selectedCompany.status ? "bloqueada" : "desbloqueada";
      toastUtils.success(`Empresa "${selectedCompany.name}" ${action} com sucesso`, "Sucesso");
      await loadData();
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      
      let errorMessage = "Erro desconhecido";
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.status === 403) {
        errorMessage = "Você não tem permissão para alterar o status desta empresa";
      } else if (error?.response?.status === 404) {
        errorMessage = "Empresa não encontrada";
      } else if (error?.response?.status === 409) {
        errorMessage = "Não é possível alterar o status desta empresa no momento";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toastUtils.error(errorMessage, "Erro ao Alterar Status");
    } finally {
      setOperationLoading(prev => ({ ...prev, block: false }));
      setLoadingState(false);
      setConfirmDialogs(prev => ({ ...prev, block: false }));
      setSelectedCompany(null);
    }
  };

  const handleCleanClick = (company) => {
    if (!company || !company.id) {
      toastUtils.error("Empresa inválida selecionada", "Erro");
      return;
    }

    if (company.id === 1) {
      toastUtils.warning(
        "Os arquivos da empresa principal (ID: 1) não podem ser limpos por questões de segurança",
        "Operação Não Permitida"
      );
      return;
    }

    if (loadingState) {
      toastUtils.info("Aguarde a operação atual terminar", "Operação em Andamento");
      return;
    }

    setSelectedCompany(company);
    setConfirmDialogs(prev => ({ ...prev, clean: true }));
  };

  const handleCleanConfirm = async () => {
    if (!selectedCompany || !selectedCompany.id) {
      toastUtils.error("Empresa não selecionada", "Erro");
      setConfirmDialogs(prev => ({ ...prev, clean: false }));
      setSelectedCompany(null);
      return;
    }

    setOperationLoading(prev => ({ ...prev, clean: true }));
    setLoadingState(true);
    
    try {
      // Use DELETE /companies/:id/files endpoint
      await cleanCompanyFiles(selectedCompany.id);
      toastUtils.success(`Arquivos da empresa "${selectedCompany.name}" limpos com sucesso`, "Sucesso");
      await loadData();
    } catch (error) {
      console.error("Erro ao limpar arquivos:", error);
      
      let errorMessage = "Erro desconhecido";
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.status === 403) {
        errorMessage = "Você não tem permissão para limpar arquivos desta empresa";
      } else if (error?.response?.status === 404) {
        errorMessage = "Empresa não encontrada";
      } else if (error?.response?.status === 409) {
        errorMessage = "Não é possível limpar os arquivos desta empresa no momento";
      } else if (error?.response?.status === 500) {
        errorMessage = "Erro interno do servidor ao limpar arquivos";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toastUtils.error(errorMessage, "Erro ao Limpar Arquivos");
    } finally {
      setOperationLoading(prev => ({ ...prev, clean: false }));
      setLoadingState(false);
      setConfirmDialogs(prev => ({ ...prev, clean: false }));
      setSelectedCompany(null);
    }
  };

  const getRowStyle = (company) => {
    if (!company || !company.dueDate) return {};

    const dueDate = moment(company.dueDate);
    if (!dueDate.isValid()) return {};

    const now = moment();
    const diff = dueDate.diff(now, "days");

    if (diff >= 1 && diff <= 5) {
      return { backgroundColor: "#fffead" };
    }
    if (diff <= 0) {
      return { backgroundColor: "#fa8c8c" };
    }
    return {};
  };

  const getPlanName = (company) => {
    if (!company || !company.planId) return "-";

    const plan = plans.find(p => p && p.id === company.planId);
    return plan && plan.name ? plan.name : "-";
  };

  const getPlanValue = (company) => {
    if (!company || !company.planId) return "-";

    const plan = plans.find(p => p && p.id === company.planId);
    if (plan && plan.amount) {
      return <FormattedCurrency value={plan.amount} />;
    }
    return "-";
  };

  return (
    <div 
      ref={containerRef}
      className={classes.root} 
      role="main" 
      aria-label="Gerenciamento de Empresas"
      aria-describedby="companies-instructions"
    >
      <div id="companies-instructions" className="sr-only">
        Página de gerenciamento de empresas. Use Ctrl+N para criar nova empresa, setas para navegar na tabela, Tab para acessar botões de ação.
      </div>
      {/* Header with title and New Company button */}
      <Box className={classes.headerContainer}>
        <Typography 
          variant="h5" 
          className={classes.headerTitle}
          component="h1"
          id="companies-title"
        >
          {i18n.t("companies.title")}
        </Typography>
        <Button
          ref={newCompanyButtonRef}
          variant="contained"
          className={classes.newCompanyButton}
          startIcon={<AddIcon />}
          onClick={handleCreateCompany}
          disabled={loadingState}
          aria-label="Criar nova empresa (Ctrl+N)"
          aria-describedby="companies-title new-company-help"
          title="Atalho: Ctrl+N"
        >
          Nova Empresa
        </Button>
        <div id="new-company-help" className="sr-only">
          Clique para abrir o formulário de criação de empresa ou use o atalho Ctrl+N
        </div>
      </Box>

      {/* Companies Table */}
      <Paper 
        className={classes.tableContainer} 
        elevation={2}
        role="region"
        aria-labelledby="companies-title"
        aria-describedby="companies-table-description"
      >
        <div 
          id="companies-table-description" 
          className="sr-only"
          aria-hidden="true"
        >
          Tabela com lista de empresas cadastradas. Use as setas do teclado para navegar entre as células e Tab para acessar os botões de ação.
        </div>
        <Table 
          ref={tableRef}
          role="table"
          aria-label={`Tabela de empresas - ${companies.length} empresas encontradas`}
        >
          <TableHead className={classes.tableHeader} role="rowgroup">
            <TableRow role="row">
              <TableCell className={classes.tableHeaderCell} role="columnheader" scope="col">#</TableCell>
              <TableCell className={classes.tableHeaderCell} role="columnheader" scope="col">{i18n.t("companies.table.name")}</TableCell>
              <TableCell className={classes.tableHeaderCell} role="columnheader" scope="col">{i18n.t("companies.table.email")}</TableCell>
              <TableCell align="center" className={classes.tableHeaderCell} role="columnheader" scope="col">{i18n.t("companies.table.phone")}</TableCell>
              <TableCell align="center" className={classes.tableHeaderCell} role="columnheader" scope="col">{i18n.t("companies.table.plan")}</TableCell>
              <TableCell align="center" className={classes.tableHeaderCell} role="columnheader" scope="col">{i18n.t("companies.table.value")}</TableCell>
              <TableCell align="center" className={classes.tableHeaderCell} role="columnheader" scope="col">{i18n.t("companies.table.active")}</TableCell>
              <TableCell align="center" className={classes.tableHeaderCell} role="columnheader" scope="col">{i18n.t("companies.table.createdAt")}</TableCell>
              <TableCell align="center" className={classes.tableHeaderCell} role="columnheader" scope="col">{i18n.t("companies.table.dueDate")}</TableCell>
              <TableCell align="center" className={classes.tableHeaderCell} role="columnheader" scope="col">{i18n.t("companies.table.lastLogin")}</TableCell>
              <TableCell align="center" className={classes.tableHeaderCell} role="columnheader" scope="col">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody role="rowgroup">
            {loading || loadingState ? (
              <TableRow role="row">
                <TableCell 
                  colSpan={11} 
                  align="center" 
                  className={classes.loadingContainer}
                  role="cell"
                  aria-live="polite"
                  aria-label="Carregando dados das empresas"
                >
                  Carregando...
                </TableCell>
              </TableRow>
            ) : companies.length === 0 ? (
              <TableRow role="row">
                <TableCell 
                  colSpan={11} 
                  align="center" 
                  className={classes.emptyContainer}
                  role="cell"
                  aria-live="polite"
                  aria-label="Nenhuma empresa encontrada"
                >
                  Nenhuma empresa encontrada
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company, index) => {
                if (!company || !company.id) return null;

                const rowStyle = getRowStyle(company);
                const isExpired = rowStyle.backgroundColor === "#fa8c8c";
                const isExpiring = rowStyle.backgroundColor === "#fffead";
                
                let statusDescription = "";
                if (isExpired) {
                  statusDescription = "Empresa com vencimento expirado";
                } else if (isExpiring) {
                  statusDescription = "Empresa próxima ao vencimento";
                }

                return (
                  <TableRow 
                    key={company.id} 
                    style={rowStyle}
                    role="row"
                    aria-rowindex={index + 2}
                    aria-label={`Empresa ${company.name || company.id}${statusDescription ? ` - ${statusDescription}` : ""}`}
                  >
                    <TableCell className={classes.tableCell} role="cell" scope="row">{company.id}</TableCell>
                    <TableCell className={classes.tableCell} role="cell">{company.name || "-"}</TableCell>
                    <TableCell className={classes.tableCell} role="cell">{company.email || "-"}</TableCell>
                    <TableCell align="center" className={classes.tableCell} role="cell">{company.phone || "-"}</TableCell>
                    <TableCell align="center" className={classes.tableCell} role="cell">{getPlanName(company)}</TableCell>
                    <TableCell align="center" className={classes.tableCell} role="cell">{getPlanValue(company)}</TableCell>
                    <TableCell align="center" className={classes.tableCell} role="cell">
                      <span
                        className={
                          company.status ? classes.activeChip : classes.blockedChip
                        }
                        aria-label={`Status: ${company.status ? "Ativa" : "Bloqueada"}`}
                      >
                        {company.status ? "Sim" : "Não"}
                      </span>
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell} role="cell">
                      <span aria-label={`Data de criação: ${dateToClient(company.createdAt)}`}>
                        {dateToClient(company.createdAt)}
                      </span>
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell} role="cell">
                      <span aria-label={`Data de vencimento: ${dateToClient(company.dueDate)}`}>
                        {dateToClient(company.dueDate)}
                        {company.recurrence && (
                          <div>
                            <small aria-label={`Recorrência: ${company.recurrence}`}>
                              {company.recurrence}
                            </small>
                          </div>
                        )}
                      </span>
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell} role="cell">
                      <span aria-label={`Último login: ${datetimeToClient(company.lastLogin) || "Nunca"}`}>
                        {datetimeToClient(company.lastLogin)}
                      </span>
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell} role="cell">
                      <ActionButtons
                        company={company}
                        onEdit={handleEditCompany}
                        onDelete={handleDeleteClick}
                        onToggleStatus={handleBlockClick}
                        onCleanFiles={handleCleanClick}
                        disabled={loadingState}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Company Modal */}
      <CompanyModal
        open={modalOpen}
        mode={modalMode}
        company={selectedCompany}
        plans={plans}
        loading={loadingState}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
      />

      {/* Confirmation Dialogs */}
      <ConfirmationModal
        title="Confirmar Exclusão"
        open={confirmDialogs.delete}
        onClose={() => {
          setConfirmDialogs(prev => ({ ...prev, delete: false }));
          setSelectedCompany(null);
        }}
        onConfirm={handleDeleteConfirm}
      >
        Tem certeza que deseja excluir a empresa{" "}
        <strong>{selectedCompany?.name}</strong> (ID: {selectedCompany?.id})?
        <br /><br />
        <strong>⚠️ ATENÇÃO:</strong> Esta ação não pode ser desfeita e removerá permanentemente:
        <br />• Todos os dados da empresa
        <br />• Usuários associados
        <br />• Configurações e histórico
      </ConfirmationModal>

      <ConfirmationModal
        title={selectedCompany?.status ? "Confirmar Bloqueio" : "Confirmar Desbloqueio"}
        open={confirmDialogs.block}
        onClose={() => {
          setConfirmDialogs(prev => ({ ...prev, block: false }));
          setSelectedCompany(null);
        }}
        onConfirm={handleBlockConfirm}
      >
        Tem certeza que deseja{" "}
        {selectedCompany?.status ? "bloquear" : "desbloquear"} a empresa{" "}
        <strong>{selectedCompany?.name}</strong> (ID: {selectedCompany?.id})?
        <br /><br />
        {selectedCompany?.status ? (
          <>
            <strong>🔒 Bloqueio:</strong> A empresa não poderá mais acessar o sistema até ser desbloqueada.
          </>
        ) : (
          <>
            <strong>🔓 Desbloqueio:</strong> A empresa voltará a ter acesso completo ao sistema.
          </>
        )}
      </ConfirmationModal>

      <ConfirmationModal
        title="Confirmar Limpeza de Arquivos"
        open={confirmDialogs.clean}
        onClose={() => {
          setConfirmDialogs(prev => ({ ...prev, clean: false }));
          setSelectedCompany(null);
        }}
        onConfirm={handleCleanConfirm}
      >
        Tem certeza que deseja limpar todos os arquivos da empresa{" "}
        <strong>{selectedCompany?.name}</strong> (ID: {selectedCompany?.id})?
        <br /><br />
        <strong>🧹 ATENÇÃO:</strong> Esta ação não pode ser desfeita e removerá permanentemente:
        <br />• Arquivos de mídia enviados
        <br />• Documentos anexados
        <br />• Imagens e vídeos armazenados
        <br />• Backups de conversas
      </ConfirmationModal>
    </div>
  );
};

export default CompaniesManager;
